import { Box } from '@mui/material';
import { StickyHeaderTabs } from 'sections/listing/sticky-header-tabs';
import { ListingActionButtons } from 'ui/action-buttons';
import { useHeaderHeight } from 'hooks/use-header-height';
import { ExperimentalSection } from 'ui/experimental-section';
import { AllowedExperimentKey } from '@tenants/types';
import { useExperimentElementVisibility, ExperimentElement } from 'experiments/element-visibility';
import type { StickyNavWithActionsProps } from './types';
import styles from './sticky-nav-with-actions.module.scss';

/**
 * StickyNavWithActions
 * 
 * Full-width sticky navigation combining:
 * - Left: Sticky header tabs (listing sections)
 * - Right: Action buttons (Share, Like) - controlled by orchestrator
 * 
 * Layout: [Tabs] ←→ [Actions]
 * 
 * Used in v2 address page when STICKY_HEADER_TABS experiment is ON.
 * Action buttons visibility is controlled by element orchestrator based on
 * active experiments and viewport.
 * 
 * Max-width dynamically matches content layout based on LISTING_HERO experiment:
 * - Control (legacy): 1200px
 * - Variation (new hero): 1400px
 * 
 * The container itself is sticky, so child tabs don't need their own sticky positioning.
 */
export function StickyNavWithActions({
  sections,
  actions,
  onTabClick,
  viewport = 'desktop',
}: StickyNavWithActionsProps) {
  const headerHeight = useHeaderHeight();
  
  // Element orchestration - determines action button visibility
  const visibility = useExperimentElementVisibility();
  const showShareButton = visibility.shouldShow(ExperimentElement.STICKY_HEADER_SHARE_BUTTON);
  const showLikeButton = visibility.shouldShow(ExperimentElement.STICKY_HEADER_LIKE_BUTTON);
  const showActions = actions && (showShareButton || showLikeButton);

  const navContent = (
    <Box className={styles.layout}>
      <Box className={styles.tabs}>
        <StickyHeaderTabs 
          sections={sections} 
          onTabClick={onTabClick}
          disableSticky={true}
          viewport={viewport}
        />
      </Box>

      {/* Right: Action buttons (orchestrator-controlled) */}
      {showActions && (
        <Box className={styles.actions}>
          <ListingActionButtons
            isLiked={actions.isLiked}
            onLikeClick={showLikeButton ? actions.onLikeClick : undefined}
            onShareClick={showShareButton ? actions.onShareClick : undefined}
            likeLabel={actions.likeLabel}
            shareLabel={actions.shareLabel}
            copiedTooltip={actions.copiedTooltip}
            showCopiedTooltip={actions.showCopiedTooltip}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box className={styles.container} sx={{ top: `${headerHeight}px` }}>
      <ExperimentalSection
        experimentKey={AllowedExperimentKey.LISTING_HERO}
        control={
          // Legacy layout: 1200px max-width
          <div className={styles.legacyContainer}>
            {navContent}
          </div>
        }
        variation={
          // New hero layout: 1400px max-width
          <div className={styles.newHeroContainer}>
            {navContent}
          </div>
        }
      />
    </Box>
  );
}
